import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

const styles = cssMap({
	container: {
		// Align with the suggestion item content width.
		paddingBlock: token('space.200'),
		paddingInline: token('space.300'),
	},
});

const i18n = defineMessages({
	loadingTextReviewing: {
		id: 'suggestions-panel.inline-loading-bar.loading-text.reviewing',
		defaultMessage: 'Rovo is reviewing your page',
		description:
			'First rotating loading message shown in the inline loading bar at the bottom of the suggestions list while Rovo is generating suggestions',
	},
	loadingTextMayTakeMinutes: {
		id: 'suggestions-panel.inline-loading-bar.loading-text.may-take-minutes',
		defaultMessage: 'This may take a few minutes',
		description:
			'Second rotating loading message shown in the inline loading bar at the bottom of the suggestions list while Rovo is generating suggestions',
	},
});

type SuggestionsInlineLoadingBarProps = {
	api?: FullPageEditorPresetAPI;
	/**
	 * Epoch ms when the thinking session started (from shared plugin state).
	 * Passed through so this bar stays in sync with the editor's thinking bar.
	 */
	startedAt?: number | null;
	/** Shared progress-bar facade target (0-100) for the thinking session. */
	progressTarget?: number | null;
};

/**
 * Compact loading bar shown at the bottom of the suggestions list while Rovo
 * regenerates with existing suggestions still visible. Reuses the editor's
 * `RovoThinkingBar` (without its gradient-border wrapper) so it stays in sync
 * with the full-page loading bar; we only own the padding and scroll-into-view.
 */
export const SuggestionsInlineLoadingBar = ({
	api,
	startedAt,
	progressTarget,
}: SuggestionsInlineLoadingBarProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const containerRef = useRef<HTMLDivElement>(null);

	const rotatingMessages = useMemo(
		() => [formatMessage(i18n.loadingTextReviewing), formatMessage(i18n.loadingTextMayTakeMinutes)],
		[formatMessage],
	);

	// Scroll into view so the bar is visible with a long list above it.
	useEffect(() => {
		containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	}, []);

	const handleStop = useCallback(() => {
		api?.aiSuggestions?.actions?.stopThinking();
	}, [api]);

	return (
		<div
			ref={containerRef}
			css={styles.container}
			data-testid="suggestions-panel-inline-loading-bar"
		>
			<RovoThinkingBar
				onStop={handleStop}
				showProgressBar
				useRotatingTitle
				customRotatingMessages={rotatingMessages}
				startedAt={startedAt}
				progressTarget={progressTarget}
			/>
		</div>
	);
};
